import { log } from "../../../common/Logging"
import { TtImage } from "./TobaccoTourAssets"

/*
    Each clickable chemical object has the following:
    - sometimes, a video before
    - sometimes, audio before
    - for each chemical
        - name of chemical
        - sprite
        - video
        - bulleted text:
            - name
            - more text
 */

export interface DoctorNoteBullet {
    text: string
    delay: number
    indent?: boolean
}

export interface TobaccoTourMolecule {
    name: string
    spriteName: string
    video: string
    x: number
    y: number
    noteStart: number
    type: TobaccoTourMoleculeTypes
    noteBullets: Array<DoctorNoteBullet>
}

export enum TobaccoTourMoleculeTypes {
    AMMONIA = "ammonia",
    ARSENIC = "arsenic",
    CARBON_MONOXIDE = "carbon-monoxide",
    FORMALDEHYDE = "formaldehyde",
    NICOTINE = "nicotine",
    NITROSAMINE = "nitrosamine",
    POLONIUM = "polonium",
}

export function createTobaccoTourMolecule(
    type: TobaccoTourMoleculeTypes,
    x: number,
    y: number,
    smokeless = false,
): TobaccoTourMolecule {
    const moleculeCigVideoTitle = (name: TobaccoTourMoleculeTypes) =>
        `tobacco-tour-${name}.mp4`
    const moleculeSmokelessVideoTitle = (name: TobaccoTourMoleculeTypes) =>
        `tobacco-tour-smokeless-${name}.mp4`

    switch (type) {
        case TobaccoTourMoleculeTypes.AMMONIA:
            return {
                x,
                y,
                type,
                noteStart: 12000,
                name: "Ammonia",
                spriteName: TtImage.AMMONIA,
                video: moleculeCigVideoTitle(TobaccoTourMoleculeTypes.AMMONIA),
                noteBullets: [
                    { text: "Irritates", delay: 1500 },
                    { text: "Skin", delay: 2000, indent: true },
                    { text: "Eyes", delay: 2500, indent: true },
                    { text: "Throat", delay: 3000, indent: true },
                    { text: "Lungs", delay: 3500, indent: true },
                    { text: "Causes coughing", delay: 4000 },
                ],
            }
        case TobaccoTourMoleculeTypes.ARSENIC:
            return {
                x,
                y,
                type,
                name: "Arsenic",
                noteStart: smokeless ? 9000 : 12000,
                spriteName: TtImage.ARSENIC,
                video: smokeless
                    ? moleculeSmokelessVideoTitle(
                          TobaccoTourMoleculeTypes.ARSENIC,
                      )
                    : moleculeCigVideoTitle(TobaccoTourMoleculeTypes.ARSENIC),
                noteBullets: smokeless
                    ? [
                          { text: "Causes cancer", delay: 1500 },
                          { text: "Skin", delay: 2000, indent: true },
                          { text: "Liver", delay: 2500, indent: true },
                          { text: "Kidneys", delay: 3000, indent: true },
                      ]
                    : [
                          { text: "Vomiting", delay: 1000 },
                          { text: "Cancer", delay: 2000 },
                          { text: "Skin", delay: 3000, indent: true },
                          { text: "Lung", delay: 3500, indent: true },
                          { text: "Liver", delay: 4000, indent: true },
                          { text: "Kidneys", delay: 4500, indent: true },
                      ],
            }
        case TobaccoTourMoleculeTypes.CARBON_MONOXIDE:
            return {
                x,
                y,
                type,
                name: "Carbon Monoxide",
                noteStart: 12000,
                spriteName: TtImage.CARBON_MONOXIDE,
                video: moleculeCigVideoTitle(
                    TobaccoTourMoleculeTypes.CARBON_MONOXIDE,
                ),
                noteBullets: [
                    { text: "Dizziness", delay: 2750 },
                    { text: "Nausea", delay: 3250 },
                    { text: "Confusion", delay: 3750 },
                ],
            }
        case TobaccoTourMoleculeTypes.FORMALDEHYDE:
            return {
                x,
                y,
                type,
                noteStart: smokeless ? 7000 : 10000,
                name: "Formaldehyde",
                spriteName: TtImage.FORMALDEHYDE,
                video: smokeless
                    ? moleculeSmokelessVideoTitle(
                          TobaccoTourMoleculeTypes.FORMALDEHYDE,
                      )
                    : moleculeCigVideoTitle(
                          TobaccoTourMoleculeTypes.FORMALDEHYDE,
                      ),
                noteBullets: smokeless
                    ? [
                          { text: "Damages mouth", delay: 1500 },
                          { text: "Hurts throat and stomach", delay: 3000 },
                          {
                              text: "Causes cancer of digestive system",
                              delay: 5000,
                          },
                      ]
                    : [
                          { text: "Breathing problems", delay: 4000 },
                          { text: "Asthma", delay: 5000 },
                          { text: "Emphysema", delay: 5500 },
                      ],
            }
        case TobaccoTourMoleculeTypes.NICOTINE:
            return {
                x,
                y,
                type,
                noteStart: smokeless ? 7000 : 10000,
                name: "Nicotine",
                spriteName: TtImage.NICOTINE,
                video: smokeless
                    ? moleculeSmokelessVideoTitle(
                          TobaccoTourMoleculeTypes.NICOTINE,
                      )
                    : moleculeCigVideoTitle(TobaccoTourMoleculeTypes.NICOTINE),
                noteBullets: smokeless
                    ? [
                          { text: "Causes nausea", delay: 2000 },
                          { text: "Raises blood pressure", delay: 2500 },
                          { text: "Limits blood flow", delay: 3000 },
                      ]
                    : [
                          { text: "Causes addiction", delay: 3000 },
                          {
                              text: "Leads to other health problems",
                              delay: 4500,
                          },
                      ],
            }
        case TobaccoTourMoleculeTypes.NITROSAMINE:
            return {
                x,
                y,
                type,
                noteStart: 10000,
                name: "Nitrosamine",
                spriteName: TtImage.NITROSAMINE,
                video: moleculeSmokelessVideoTitle(
                    TobaccoTourMoleculeTypes.NITROSAMINE,
                ),
                noteBullets: [
                    { text: "Causes cancer", delay: 1000 },
                    { text: "Head and neck", delay: 2000, indent: true },
                    { text: "Digestive system", delay: 3000, indent: true },
                    { text: "Bladder", delay: 4000, indent: true },
                ],
            }
        case TobaccoTourMoleculeTypes.POLONIUM:
            return {
                x,
                y,
                type,
                noteStart: 9500,
                name: "Polonium-210",
                spriteName: TtImage.POLONIUM,
                video: smokeless
                    ? moleculeSmokelessVideoTitle(
                          TobaccoTourMoleculeTypes.POLONIUM,
                      )
                    : moleculeCigVideoTitle(TobaccoTourMoleculeTypes.POLONIUM),
                noteBullets: smokeless
                    ? [
                          { text: "Causes tumors", delay: 2000 },
                          { text: "Causes cancer", delay: 2500 },
                          { text: "Mouth", delay: 3000, indent: true },
                          { text: "Stomach", delay: 3500, indent: true },
                          { text: "Bladder", delay: 4000, indent: true },
                      ]
                    : [
                          { text: "Causes", delay: 1000 },
                          { text: "Tumors", delay: 2000, indent: true },
                          { text: "Cancer", delay: 2500, indent: true },
                      ],
            }
        default:
            log.error("Tobacco molecule type not recognized.")
    }
}
